import { clientApi } from '~/src/utils/clientApi';
import { sendErrorToSentry } from '~/src/utils/sentry';

export default defineNuxtPlugin((nuxtApp) => {
  const { token } = useAuth();
  const runtime = useRuntimeConfig();
  const absoluteBaseURL = runtime.public.apiBaseUrl as string;
  if (!absoluteBaseURL) throw new Error('NUXT_PUBLIC_API_BASE_URL is not defined');

  // Update clientApi token when auth token changes
  watch(
    token,
    (newToken) => {
      clientApi.setToken(newToken || null);
    },
    { immediate: true }
  );
  // Ensure clientApi always targets the production API domain
  clientApi.setBaseURL(absoluteBaseURL);

  // Legacy API methods for backward compatibility
  const createApiInstance = () => {
    return $fetch.create({
      baseURL: absoluteBaseURL, // Force absolute prod API base
      headers: {
        Authorization: token.value || '',
      },
      async onResponseError({ response, request, options }) {
        // Отправка в Sentry
        try {
          const status = response.status || 0;
          const isServerError = status >= 400;

          if (isServerError) {
            const url = typeof request === 'string' ? request : request.url || 'unknown';
            const method = options.method || 'GET';

            sendErrorToSentry(
              {
                status,
                message: response.statusText || 'Unknown error',
                code: String(status),
                data: response._data,
              },
              url,
              method,
              options.body,
              options.headers as Record<string, string>,
              response._data
            );
          }
        } catch (sentryError) {
          // Игнорируем ошибки Sentry
          console.error('[API Plugin] Ошибка при обработке ошибки:', sentryError);
        }

        // Обработка 401 ошибки
        if (response.status === 401) {
          await nuxtApp.runWithContext(() => navigateTo('/login'));
        }
      },
    });
  };

  const api = {
    get: <T>(url: string, opts: Record<string, unknown> = {}) => {
      const instance = createApiInstance();
      return instance<T>(url, { ...opts, method: 'GET' });
    },
    post: <T>(
      url: string,
      body?: Record<string, unknown> | BodyInit,
      opts: Record<string, unknown> = {}
    ) => {
      const instance = createApiInstance();
      return instance<T>(url, { ...opts, method: 'POST', body });
    },
    put: <T>(
      url: string,
      body?: Record<string, unknown> | BodyInit,
      opts: Record<string, unknown> = {}
    ) => {
      const instance = createApiInstance();
      return instance<T>(url, { ...opts, method: 'PUT', body });
    },
    delete: <T>(url: string, opts: Record<string, unknown> = {}) => {
      const instance = createApiInstance();
      return instance<T>(url, { ...opts, method: 'DELETE' });
    },
  };

  return {
    provide: {
      api,
      clientApi,
    },
  };
});
